import React, { useState } from 'react'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { serverMessage } from '../../../../shared/sso-workspace/format'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { createChannel } from './alert-routing-api'
import { leaveFor, savePending } from './connect-pending'
import { providerLabel } from './connection-field'

// Slack's own rule for a channel name, checked as it is typed (the server
// checks it again: channel_connection.name_invalid).
export const CHANNEL_NAME = /^[a-z0-9][a-z0-9_-]{0,79}$/
export const CHANNEL_NAME_RULE = 'Lowercase letters, numbers, - and _, up to 80 characters, no spaces'
const clean = name => (name || '').trim().replace(/^#/, '')

const codeOf = err => (err && err.data && err.data.code) || ''

// + New channel (the channel picker's docked footer, or its "+ Create #name"
// with the typed name made a channel name): a name, public or private,
// and Create channel. The app creates it in the connected workspace and the
// picker picks it (onCreated). A workspace that hasn't allowed the app to
// create channels yet answers 409 needs_scope with a consent page: the
// channel waits with the target's draft (connect-pending.js) and the page
// leaves for that consent; the return page then creates it once. Never from
// the route editor (inline): leaving would lose the unsaved route, so it
// says what the server said. A name that is taken offers "Use #name" when
// the app can see that channel. The shared confirmation's frame, with the
// scrim (CLAUDE.md, "Reveal-once secrets").
//
// pending: { provider, returnTo, draft }; channelKey: the channel field's key.
export const NewChannelDialog = ({ open, connectionID, channelKey, initialName = '', inline = false, pending = {}, onCreated, onCancel }) => {
  const [name, setName] = useState(initialName)
  const [isPrivate, setPrivate] = useState(false)
  const [busy, setBusy] = useState(false)
  const [taken, setTaken] = useState(null) // { existingChannelID } once a create says the name is taken
  const [error, failure, setError] = useErrorLine()
  const label = providerLabel(pending.provider)
  const typed = clean(name)
  const valid = CHANNEL_NAME.test(typed)

  const create = async () => {
    if (!valid || busy) return
    setBusy(true)
    setError(null)
    setTaken(null)
    try {
      const out = await createChannel(connectionID, { name: typed, private: isPrivate })
      setBusy(false)
      onCreated(out.channel)
    } catch (err) {
      const code = codeOf(err)
      if (code === 'channel_connection.needs_scope' && !inline && err.data.authorizeURL) {
        savePending({ provider: pending.provider, purpose: 'add_scope', returnTo: pending.returnTo, draft: pending.draft,
          newChannel: { name: typed, private: isPrivate, key: channelKey } })
        leaveFor(err.data.authorizeURL)
        return
      }
      setBusy(false)
      if (code === 'channel_connection.name_taken') setTaken({ name: typed, existingChannelID: err.data.existingChannelID || null })
      else setError(serverMessage(err, "The channel couldn't be created."), err)
    }
  }

  // The dialog is a React child of the drawer it was opened from (portalled,
  // but its events still bubble through the drawer): Escape stops here, so it
  // closes only the dialog and never the drawer with the target's draft.
  const onKeyDown = e => {
    if (e.key !== 'Escape') return
    e.stopPropagation()
    onCancel()
  }

  return (
    <div className="ar-contents" onKeyDown={onKeyDown}>
      <ConfirmDialog open={open} scrim title={`New ${label} channel`} confirmText="Create channel" confirmClassName="btn sw-btn-primary"
        body={`EducationXR creates the channel in ${label} and joins it.`} busy={busy} confirmDisabled={!valid}
        error={error} failure={failure} onConfirm={create} onCancel={onCancel}>
        <div className="ar-form mb-3">
          <div className="sw-field">
            <label className="sw-label" htmlFor="ar-new-channel-name">Channel name</label>
            <input id="ar-new-channel-name" className={`form-control${typed && !valid ? ' is-invalid' : ''}`} autoComplete="off" spellCheck={false}
              aria-describedby="ar-new-channel-rule" value={name} placeholder="ops-alerts"
              onChange={e => { setName(e.target.value); setTaken(null) }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); create() } }} />
            <div id="ar-new-channel-rule" className={`sw-hint${typed && !valid ? ' text-danger' : ''}`}>{CHANNEL_NAME_RULE}</div>
          </div>
          <div role="radiogroup" aria-label="Who can see it">
            <div className="form-check">
              <input id="ar-new-channel-public" className="form-check-input" type="radio" name="ar-new-channel-kind" checked={!isPrivate} onChange={() => setPrivate(false)} />
              <label className="form-check-label" htmlFor="ar-new-channel-public">Public</label>
            </div>
            <div className="form-check">
              <input id="ar-new-channel-private" className="form-check-input" type="radio" name="ar-new-channel-kind" checked={isPrivate} onChange={() => setPrivate(true)} />
              <label className="form-check-label" htmlFor="ar-new-channel-private">Private</label>
            </div>
            <p className="sw-hint m-0">Private: only the people invited to it can see it.</p>
          </div>
          {taken &&
            <div className="ar-row">
              <p className="text-danger small m-0" role="alert">That name is taken.</p>
              {taken.existingChannelID &&
                <button type="button" className="btn sw-btn btn-sm" onClick={() => onCreated({ id: taken.existingChannelID, name: taken.name, private: isPrivate })}>
                  {`Use #${taken.name}`}
                </button>}
            </div>}
        </div>
      </ConfirmDialog>
    </div>
  )
}
